import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "YOUR_ACCESS_KEY_HERE";

const SERVICE_OPTIONS = [
  "Oil Change & Fluids",
  "Brake Service",
  "Engine Diagnostics",
  "High-Mileage Care",
  "General Maintenance",
  "Full Repairs",
  "Other / Not Sure",
];

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle_location: "",
    vehicle_info: "",
    primary_issue: "",
    message: "",
    contact_method: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: "New Quote Request from " + formData.name,
        from_name: "Mobile Mechanic PDX",
        ...formData,
      };

      await axios.post("https://api.web3forms.com/submit", payload);
      setStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        vehicle_location: "",
        vehicle_info: "",
        primary_issue: "",
        message: "",
        contact_method: "",
      });
    } catch (error) {
      console.error("Web3Forms Submission Error:", error);
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="uppercase text-xs tracking-[0.2em] text-muted-foreground font-medium mb-4"
        >
          Request a Quote
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none mb-16 max-w-xl"
        >
          Skip the tow truck. <span className="text-primary">We bring the garage to you.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {status === "success" ? (
              <div className="bg-card border border-stone-200 rounded-xl p-10 text-center" data-testid="contact-success">
                <CheckCircle weight="duotone" className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-2xl mb-2">Quote Requested!</h3>
                <p className="text-muted-foreground mb-6">
                  We've received your details and will get back to you shortly with an estimate.
                </p>
                <Button
                  onClick={() => setStatus("idle")}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="contact-send-another"
                >
                  Send Another Request
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      required
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      placeholder="you@email.com"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      placeholder="(503) 482-8686"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location" className="text-sm font-medium mb-2 block">
                      Vehicle Location (Address) *
                    </Label>
                    <Input
                      id="location"
                      required
                      placeholder="Street, City, Zip"
                      value={formData.vehicle_location}
                      onChange={(e) => handleChange("vehicle_location", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="vehicle_info" className="text-sm font-medium mb-2 block">
                      Vehicle Info (Year/Make/Model) *
                    </Label>
                    <Input
                      id="vehicle_info"
                      required
                      placeholder="e.g. 2018 Toyota RAV4"
                      value={formData.vehicle_info}
                      onChange={(e) => handleChange("vehicle_info", e.target.value)}
                      className="bg-card border-stone-200 focus:ring-primary rounded-xl h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="primary_issue" className="text-sm font-medium mb-2 block">
                      Primary Issue *
                    </Label>
                    <Select
                      value={formData.primary_issue}
                      onValueChange={(val) => handleChange("primary_issue", val)}
                    >
                      <SelectTrigger className="bg-card border-stone-200 rounded-xl h-11">
                        <SelectValue placeholder="Select an issue" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Car Won't Start">Car Won't Start</SelectItem>
                        <SelectItem value="Brake Squeal/Issues">Brake Squeal/Issues</SelectItem>
                        <SelectItem value="Routine Maintenance">Routine Maintenance</SelectItem>
                        <SelectItem value="Pre-Purchase Inspection">Pre-Purchase Inspection</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium mb-2 block">
                    Brief Description of Problem *
                  </Label>
                  <Textarea
                    id="message"
                    required
                    rows={4}
                    placeholder="Tell us more about what's going on with your vehicle..."
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    className="bg-card border-stone-200 focus:ring-primary rounded-xl"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-3 block">Preferred Contact Method *</Label>
                  <div className="flex flex-wrap gap-4">
                    {["Phone", "Email", "Text"].map((method) => (
                      <label key={method} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="radio"
                          name="contact_method"
                          required
                          value={method}
                          checked={formData.contact_method === method}
                          onChange={(e) => handleChange("contact_method", e.target.value)}
                          className="w-4 h-4 text-primary border-stone-300 focus:ring-primary"
                        />
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          {method}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-sm">
                    Something went wrong. Please try again or call us directly.
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={status === "loading"}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] transition-transform font-heading font-bold text-base px-8 h-12 w-full sm:w-auto"
                >
                  {status === "loading" ? "Sending..." : "Request Quote"}
                </Button>
              </form>
            )}
          </motion.div>

          {/* Map & Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="space-y-6"
          >
            {/* Google Map */}
            <div
              className="rounded-xl overflow-hidden border border-stone-200 h-[280px] lg:h-[320px]"
              data-testid="contact-map"
            >
              <iframe
                title="Mobile Mechanic PDX Location"
                src="https://www.google.com/maps?q=15206+NW+Sweetgale+Ln,+Portland,+OR+97229&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-card border border-stone-200 rounded-xl p-5">
                <MapPin weight="duotone" className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading font-bold text-sm mb-1">Address</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  15206 NW Sweetgale Ln,
                  <br />
                  Portland, OR 97229
                </p>
              </div>
              <div className="bg-card border border-stone-200 rounded-xl p-5">
                <Phone weight="duotone" className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading font-bold text-sm mb-1">Phone</p>
                <a
                  href="tel:+15034828686"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  (503) 482-8686
                </a>
              </div>
              <div className="bg-card border border-stone-200 rounded-xl p-5">
                <Clock weight="duotone" className="w-5 h-5 text-primary mb-3" />
                <p className="font-heading font-bold text-sm mb-1">Hours</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Mon: 5:30am–5:30pm
                  <br />
                  Sun: 9:00am–3:00pm
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
